/*
 * What happened, in the bottom-right corner, for a few seconds (VISUAL.md §6.10). A toast is the
 * *visible* half of something the interface has already said out loud: every action that leaves the
 * screen unchanged — *Copy link*, *Pin*, *Mark all as read* — announced itself to a screen reader
 * and showed an eye nothing at all, so a copy that worked was indistinguishable from one that
 * silently failed.
 *
 * Three decisions are worth knowing.
 *
 * **Each toast carries its own role and the host carries none.** A completed action is a
 * `role="status"`, a refusal a `role="alert"`: the urgency belongs to the message rather than to the
 * container. One live region around both would either interrupt for a "Copied" or stay polite about
 * a failure, and an alert nested inside a status region is announced twice.
 *
 * **It stands clear of the bottom of the screen by measurement, not by guess.** A custom property
 * published by the composer would have been cheaper, and it cannot work: one inherits downwards only
 * and this host is a child of the application root — and on a narrow window the rail is a bar *under*
 * the composer, so the composer's own height is the wrong number anyway. The host therefore measures
 * how far from the foot of the window the controls at the bottom begin, while a toast is on screen:
 * the composer, and anything else that marks itself as a floor (`TOAST_FLOOR`) — the bottom bar on a
 * phone, a side panel's footer with its main button. Without it a toast covers the send button or a
 * *Commit*, which is the one control underneath it.
 *
 * **An error never expires.** A TTL is a promise that the message was read, and nothing in a browser
 * can keep it: what succeeded goes away by itself, what failed waits to be dismissed.
 */

import type { CSSProperties, ReactNode } from "react"
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { Button } from "../../ui/index.ts"
import { t, translate, type AnyKey, type Args, type Key } from "../i18n/index.ts"
import "./feedback.css"
import { Icon, type IconName } from "./icons.tsx"

/**
 * The attribute an element carries to say "a toast stands above me": a row of controls pinned to
 * the foot of the window whose main button a toast in the corner would otherwise cover. A module
 * marks its own footer with it; core knows no module's class names.
 */
export const TOAST_FLOOR = "data-toast-floor"

/** How long a toast that reports a success stays. An error is given no TTL and stays. */
export const TOAST_TTL_MS = 4000

/** How many toasts are drawn at once; everything older than these is counted into "+n". */
export const TOAST_STACK_MAX = 3

/**
 * What kind of thing happened. `info` and `success` are read politely and expire; `error` interrupts
 * and stays. The kind decides the glyph and its colour and nothing else — a toast floats, and a
 * floating surface with a tinted ground reads as a panel of its own rather than as a message.
 */
export type ToastKind = "info" | "success" | "error"

/** The one thing a toast may offer — *Undo*, *Retry*, *Show me*. Taking it dismisses the toast. */
export interface ToastAction {
	key: AnyKey
	onClick(): void
}

/*
 * A module's own key is accepted exactly as the announcer accepts one (`Announcer.tsx`): a key of
 * this build is typed down to its placeholders, and a key that arrives with a module's route bundle
 * takes a plain argument record, because `translate()` renders both. A module that may speak to a
 * reader is also the one that has to be able to report a refusal.
 */
export interface ToastInput<K extends AnyKey = AnyKey> {
	kind: ToastKind
	key: K
	args?: K extends Key ? Args<K> : Record<string, string | number>
	action?: ToastAction
	/** Milliseconds. Ignored for an error, which never expires. */
	ttl?: number
}

/**
 * The one call: `toast({ kind: "success", key: "chat.message.copied" })`. It answers what takes that
 * toast away early: a toast that says something is under way ("Opening as a document …") is gone
 * the moment the answer it waited for has come, instead of standing beside it until its TTL.
 */
export type Toast = <K extends AnyKey>(input: ToastInput<K>) => () => void

interface Entry {
	id: number
	kind: ToastKind
	text: string
	action: ToastAction | undefined
	/** The life this toast was given, so the draining hairline and the timer cannot disagree. */
	ttl: number | undefined
}

/** The glyph each kind is read by, in front of its sentence. */
const GLYPH: Record<ToastKind, IconName> = {
	info: "info",
	success: "circleCheck",
	error: "triangleAlert",
}

const ToastContext = createContext<Toast>(() => () => undefined)

export function ToastProvider({ children }: { children: ReactNode }) {
	const [toasts, setToasts] = useState<Entry[]>([])
	const nextId = useRef(1)
	const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

	useEffect(
		() => () => {
			for (const timer of timers.current.values()) clearTimeout(timer)
			timers.current.clear()
		},
		[],
	)

	const dismiss = useCallback((id: number) => {
		const timer = timers.current.get(id)
		if (timer !== undefined) clearTimeout(timer)
		timers.current.delete(id)
		setToasts((current) => current.filter((entry) => entry.id !== id))
	}, [])

	const toast = useCallback<Toast>(
		({ kind, key, args, action, ttl }) => {
			const id = nextId.current++
			const life = kind === "error" ? undefined : (ttl ?? TOAST_TTL_MS)
			setToasts((current) => [
				...current,
				{ id, kind, text: translate(key, args), action, ttl: life },
			])
			if (life !== undefined) {
				timers.current.set(
					id,
					setTimeout(() => {
						timers.current.delete(id)
						setToasts((current) => current.filter((entry) => entry.id !== id))
					}, life),
				)
			}
			return () => dismiss(id)
		},
		[dismiss],
	)

	// The newest are the ones on screen: an older message still waiting to be dismissed is counted
	// rather than drawn, so a burst of four cannot climb the screen and over the conversation band.
	const shown = toasts.slice(-TOAST_STACK_MAX)

	return (
		<ToastContext.Provider value={toast}>
			{children}
			<ToastStack shown={shown} hidden={toasts.length - shown.length} dismiss={dismiss} />
		</ToastContext.Provider>
	)
}

function ToastStack({
	shown,
	hidden,
	dismiss,
}: {
	shown: readonly Entry[]
	hidden: number
	dismiss: (id: number) => void
}) {
	const bottomChrome = useBottomChrome(shown.length > 0)

	// Nothing to say is nothing in the tree, and no observers watching the document either: the
	// measurement below exists for the seconds a card is up, not for the afternoon in between.
	if (shown.length === 0) return null

	return (
		<div
			className="tb-toasts"
			style={{ "--tb-bottom-chrome": `${bottomChrome}px` } as CSSProperties}
		>
			{hidden > 0 ? (
				<p className="tb-toasts__more">{t("shell.toast.more", { count: hidden })}</p>
			) : null}
			{shown.map((entry) => (
				<div
					key={entry.id}
					className={`tb-elevated tb-toast tb-toast--${entry.kind}`}
					role={entry.kind === "error" ? "alert" : "status"}
					// What every float in the product is stamped with when it arrives: `.tb-elevated`'s
					// own rules (ui.css) read it and give the card the shared entrance.
					data-state="open"
					style={
						entry.ttl === undefined
							? undefined
							: ({ "--tb-ttl": `${entry.ttl}ms` } as CSSProperties)
					}
				>
					<span className="tb-toast__glyph">
						<Icon name={GLYPH[entry.kind]} />
					</span>
					<span className="tb-toast__text">{entry.text}</span>
					{entry.action === undefined ? null : (
						<Button
							variant="ghost"
							size="sm"
							className="tb-toast__action"
							label={translate(entry.action.key)}
							onClick={() => {
								entry.action?.onClick()
								dismiss(entry.id)
							}}
						/>
					)}
					<Button
						variant="ghost"
						size="sm"
						quiet
						icon
						className="tb-toast__dismiss"
						aria-label={t("shell.toast.dismiss")}
						before={<Icon name="close" size={16} />}
						onClick={() => dismiss(entry.id)}
					/>
					{/* The time running out, drawn rather than counted: two pixels of accent along the
					    foot of the card. An error has no TTL and therefore no bar. */}
					{entry.ttl === undefined ? null : <span className="tb-toast__drain" aria-hidden="true" />}
				</div>
			))}
		</div>
	)
}

/**
 * How much of the bottom of the window the composer and everything under it take up, in pixels —
 * the toast's floor. Zero where there is no composer and no other floor (Home, Activity, the
 * directory), and the largest of them where there are two (a conversation with the thread pane open
 * beside it, a page with its panel's footer).
 *
 * It is measured as the distance from the composer's top edge to the foot of the window rather than
 * as the composer's own height, and that is the whole point: on a narrow window the rail is a bar
 * *under* the composer, so a toast lifted by the composer's height alone lands squarely on the box
 * it is reporting about. What has to be cleared is everything below the writing box, whatever that
 * turns out to be.
 *
 * The observers exist only while a toast is on screen. A `MutationObserver` over the document is
 * cheap for four seconds and wasteful for an afternoon, and the *set* of composers is what changes
 * when a thread opens — a size observer alone would never see it.
 */
function useBottomChrome(active: boolean): number {
	const [chrome, setChrome] = useState(0)

	useEffect(() => {
		if (!active) return undefined
		if (typeof ResizeObserver === "undefined" || typeof MutationObserver === "undefined") {
			return undefined
		}
		let observed: Element[] = []

		function measure(): void {
			let deepest = 0
			for (const region of observed) {
				deepest = Math.max(deepest, window.innerHeight - region.getBoundingClientRect().top)
			}
			setChrome(Math.max(0, Math.round(deepest)))
		}

		const sizes = new ResizeObserver(measure)

		function scan(): void {
			const found = [...document.querySelectorAll(`.tb-composer, [${TOAST_FLOOR}]`)]
			if (found.length === observed.length && found.every((el, i) => el === observed[i])) {
				measure()
				return
			}
			sizes.disconnect()
			observed = found
			for (const region of found) sizes.observe(region)
			measure()
		}

		const tree = new MutationObserver(scan)
		tree.observe(document.body, { childList: true, subtree: true })
		// A window that is resized moves the composer's top edge without changing its height, which a
		// size observer does not report.
		window.addEventListener("resize", measure)
		scan()
		return () => {
			tree.disconnect()
			sizes.disconnect()
			window.removeEventListener("resize", measure)
		}
	}, [active])

	return chrome
}

/** `toast({ kind: "error", key: "chat.menu.failed" })`. */
export function useToast(): Toast {
	return useContext(ToastContext)
}
